/**
 * Browser-side Google Distance Matrix (uses the same Maps JS key as Places autocomplete).
 */

import { ensureGoogleMapsLoaded } from './addressAutocomplete';
import type { DrivingDurationInput } from './calendarEventGroupPlan';
import { formatMatrixLocation } from './drivingDurationEstimate';

export async function getClientDrivingDuration(
  input: DrivingDurationInput,
): Promise<{ durationSeconds: number; distanceMeters?: number }> {
  const ready = await ensureGoogleMapsLoaded();
  if (!ready) {
    throw new Error('Google Maps JavaScript API not available');
  }

  const g = (window as typeof window & { google: typeof google }).google;

  const origin =
    input.originLat != null && input.originLng != null
      ? new g.maps.LatLng(input.originLat, input.originLng)
      : formatMatrixLocation(input.origin, input.originLat, input.originLng);

  const destination =
    input.destinationLat != null && input.destinationLng != null
      ? new g.maps.LatLng(input.destinationLat, input.destinationLng)
      : formatMatrixLocation(input.destination, input.destinationLat, input.destinationLng);

  const request: google.maps.DistanceMatrixRequest = {
    origins: [origin],
    destinations: [destination],
    travelMode: g.maps.TravelMode.DRIVING,
  };

  const withTraffic: google.maps.DistanceMatrixRequest = {
    ...request,
    drivingOptions: {
      departureTime: new Date(),
      trafficModel: g.maps.TrafficModel.BEST_GUESS,
    },
  };

  const run = (req: google.maps.DistanceMatrixRequest) =>
    new Promise<{ durationSeconds: number; distanceMeters?: number }>((resolve, reject) => {
      const service = new g.maps.DistanceMatrixService();
      service.getDistanceMatrix(req, (response, status) => {
        if (status !== g.maps.DistanceMatrixStatus.OK || !response) {
          reject(new Error(`Distance Matrix error: ${status}`));
          return;
        }
        const element = response.rows[0]?.elements[0];
        if (!element || element.status !== g.maps.DistanceMatrixElementStatus.OK) {
          reject(new Error(`Distance Matrix error: ${element?.status || 'NO_ROUTE'}`));
          return;
        }
        resolve({
          durationSeconds: element.duration_in_traffic?.value ?? element.duration?.value ?? 0,
          distanceMeters: element.distance?.value,
        });
      });
    });

  try {
    return await run(withTraffic);
  } catch {
    return run(request);
  }
}
